import {
  Box,
  Card,
  CardContent,
  Collapse,
  Grid2 as Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import PhonelinkSetupIcon from "@mui/icons-material/PhonelinkSetup";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RenderCustomerContractors from "./RenderCustomerContractors";
import RenderDiscounts from "./RenderDiscounts";
import RenderUsageChart from "./RenderUsageChart";
import RenderCustomerPermits from "./RenderCustomerPermits";
import RemoteConnectionModal from "./managementModals/RemoteConnectionModal";

const CustomerInfoCard = ({
  customerSelectedRow,
  setCustomerSelectedRow,
  packagePage,
  setPackagePage,
  packagePageSize,
  setpackagePageSize,
  packageLoading,
  setPackageLoading,
  packageRowCount,
  setPackageRowCount,
  selectedPackage,
  setSelectedPackage,
}) => {
  const [tables, setTables] = useState([]);
  const [cardPage, setCardPage] = useState(1);
  const [discountPage, setDiscountPage] = useState(1);
  const [remoteModalOpen, setRemoteModalOpen] = useState(false);
  const [remoteCredentials, setRemoteCredentials] = useState({
    username: "",
    usernameError: false,
    password: "",
    passwordError: false,
  });
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [connectionMsg, setConnectionMsg] = useState("");
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const customersPagination = useSelector(
    (state) => state.nocData?.customers?.pagination
  );

  const closeRemoteModal = () => {
    setRemoteModalOpen(false);
    setRemoteCredentials({
      username: "",
      usernameError: false,
      password: "",
      passwordError: false,
    });
    setConnectionMsg("");
  };

  const handleRemoteConnect = async () => {
    try {
      setConnectionLoading(true);
      setConnectionMsg("");

      // Clear previous errors
      setRemoteCredentials((prev) => ({
        ...prev,
        usernameError: false,
        passwordError: false,
      }));

      // Validate inputs
      if (!remoteCredentials.username.trim()) {
        setRemoteCredentials((prev) => ({ ...prev, usernameError: true }));
        return;
      }

      if (!remoteCredentials.password.trim()) {
        setRemoteCredentials((prev) => ({ ...prev, passwordError: true }));
        return;
      }

      if (!customerSelectedRow?.email) {
        setConnectionMsg("Customer email is missing");
        return;
      }

      // Persist current admin Customers grid state so we can restore it on EXIT
      try {
        const gridState = {
          page: customersPagination?.page ?? 0,
          pageSize: customersPagination?.rowsPerPage ?? 5,
          selectedCustomerId: customerSelectedRow?.id ?? null,
        };
        sessionStorage.setItem(
          "adminRemoteCustomersState",
          JSON.stringify(gridState)
        );
      } catch (e) {
        // ignore storage errors
      }

      // Validate remote credentials
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/remote-connection`,
        {
          currentSession: currentSession,
          email: customerSelectedRow.email,
          username: remoteCredentials.username.trim(),
          password: remoteCredentials.password.trim(),
        },
        { withCredentials: true }
      );

      if (resp?.data?.success) {
        setConnectionMsg("Credentials validated! Creating remote session...");

        // Create the remote session in the SAME tab
        const sessionResp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/admin/remote-connection-session`,
          {
            currentSession: currentSession,
            target_email: customerSelectedRow.email,
          },
          { withCredentials: true }
        );

        if (sessionResp?.data?.success) {
          // Mark this browser tab as being in an admin remote session
          try {
            sessionStorage.setItem("adminRemote", "true");
            if (currentUser?.user?.email) {
              sessionStorage.setItem(
                "adminRemoteEmail",
                currentUser.user.email
              );
            }
          } catch (e) {
            // ignore storage errors
          }

          // Navigate to dashboard as the remote user
          const baseUrl = window.location.origin;
          window.location.href = `${baseUrl}/dashboard`;
        } else {
          setConnectionMsg(
            sessionResp?.data?.message ||
              "Failed to create remote session. Please try again."
          );
        }
      } else {
        setConnectionMsg(
          resp?.data?.message || "Connection failed. Please try again."
        );
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Connection failed. Please try again.";
      setConnectionMsg(errorMessage);
      console.error("Remote connection error:", error);
    } finally {
      setConnectionLoading(false);
      setTimeout(() => {
        setConnectionMsg("");
      }, 8000);
    }
  };

  useEffect(() => {
    setTables((prevTables) => {
      // Only update if customerSelectedRow actually changed (different ID)
      const currentRowId = customerSelectedRow?.id;
      const prevRowId = prevTables[0]?.rowId;

      if (currentRowId === prevRowId) {
        // Same row, just update the data without changing structure
        return prevTables.map((table) => {
          switch (table.header) {
            case "Permit Packages":
              return { ...table, data: customerSelectedRow?.permitPackages };
            case "Personal":
              return {
                ...table,
                data: customerSelectedRow?.tableDatas?.tableDataPersonal,
              };
            case "Plan":
              return {
                ...table,
                data: customerSelectedRow?.tableDatas?.tableDataPlan,
              };
            case "Usage":
              return {
                ...table,
                data: customerSelectedRow?.tableDatas?.tableDataUsage,
              };
            case "Contractors":
              return { ...table, data: customerSelectedRow?.contractors };
            case "Coupons":
              return {
                ...table,
                data: customerSelectedRow?.user_discounts,
                coupons: customerSelectedRow?.coupons,
              };
            case "Permit Package Usage Chart":
              return { ...table, data: customerSelectedRow?.ppUsage };
            case "Notice of Commencement Usage Chart":
              return { ...table, data: customerSelectedRow?.nocUsage };
            default:
              return table;
          }
        });
      } else {
        // Different row, create new structure
        return [
          {
            name: "permitPackages",
            header: "Permit Packages",
            open: false,
            data: customerSelectedRow?.permitPackages,
            rowId: currentRowId,
          },
          {
            header: "Personal",
            open: false,
            data: customerSelectedRow?.tableDatas?.tableDataPersonal,
            rowId: currentRowId,
          },
          {
            header: "Plan",
            open: false,
            data: customerSelectedRow?.tableDatas?.tableDataPlan,
            rowId: currentRowId,
          },
          {
            header: "Usage",
            open: false,
            data: customerSelectedRow?.tableDatas?.tableDataUsage,
            rowId: currentRowId,
          },
          {
            header: "Contractors",
            open: false,
            data: customerSelectedRow?.contractors,
            rowId: currentRowId,
          },
          {
            header: "Coupons",
            open: false,
            data: customerSelectedRow?.user_discounts,
            coupons: customerSelectedRow?.coupons,
            rowId: currentRowId,
          },
          {
            name: "ppUsage",
            header: "Permit Package Usage Chart",
            open: false,
            data: customerSelectedRow?.ppUsage,
            rowId: currentRowId,
          },
          {
            name: "nocUsage",
            header: "Notice of Commencement Usage Chart",
            open: false,
            data: customerSelectedRow?.nocUsage,
            rowId: currentRowId,
          },
        ];
      }
    });
  }, [customerSelectedRow]);

  const toggleTableOpen = (header) => {
    setTables((prevTables) =>
      prevTables.map((table) =>
        table.header === header ? { ...table, open: !table.open } : table
      )
    );
  };

  const isTableOpen = (header) => {
    const table = tables.find((table) => table.header === header);
    return table ? table.open : false; // Returns `false` if not found
  };

  return (
    <Box sx={{ mt: "1rem" }}>
      <Card>
        <CardContent sx={{ pt: 2 }}>
          <Box mb={3} textAlign="center">
            <Typography variant="h5" mb={0} gutterBottom>
              {customerSelectedRow?.customerName}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              {customerSelectedRow?.email}
            </Typography>
            <Tooltip title="Connect to account">
              <IconButton
                aria-label="Open remote connection modal"
                onClick={() => setRemoteModalOpen(true)}
                sx={{
                  color: "success.main",
                  border: "1px solid",
                  borderColor: "success.main",
                  mt: 1,
                }}
              >
                <PhonelinkSetupIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Grid container spacing={2}>
            {tables.map((table) => (
              <Grid
                key={table?.header}
                size={{
                  xs: 12,
                  sm:
                    table?.header === "Coupons" ||
                    ["ppUsage", "nocUsage", "permitPackages"].includes(
                      table?.name
                    )
                      ? 12
                      : 6,
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ position: "relative" }}>
                          <TableCell
                            sx={
                              table?.header === "Coupons" ||
                              ["ppUsage", "nocUsage"].includes(table?.name)
                                ? {
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    width: "100%",
                                    height: "40px",
                                  }
                                : { fontWeight: "bold" }
                            }
                          >
                            {table?.header}
                          </TableCell>
                          <TableCell
                            align={
                              table?.header !== "Coupons" &&
                              !["ppUsage", "nocUsage"].includes(table?.name)
                                ? "right"
                                : undefined
                            }
                            sx={
                              table?.header === "Coupons" ||
                              ["ppUsage", "nocUsage"].includes(table?.name)
                                ? { position: "absolute", right: 16 }
                                : {}
                            }
                          >
                            <IconButton
                              onClick={() => toggleTableOpen(table?.header)}
                            >
                              {isTableOpen(table?.header) ? (
                                <RemoveCircleIcon color="error" />
                              ) : (
                                <AddCircleIcon color="primary" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{
                              p: 0,
                            }}
                          >
                            <Collapse
                              in={isTableOpen(table?.header)}
                              timeout="auto"
                              unmountOnExit
                            >
                              {table?.header === "Contractors" ? (
                                <RenderCustomerContractors
                                  cardPage={cardPage}
                                  setCardPage={setCardPage}
                                  table={table}
                                />
                              ) : table?.header === "Coupons" ? (
                                <RenderDiscounts
                                  discountPage={discountPage}
                                  setDiscountPage={setDiscountPage}
                                  table={table}
                                  customerSelectedRow={customerSelectedRow}
                                  setCustomerSelectedRow={
                                    setCustomerSelectedRow
                                  }
                                />
                              ) : ["ppUsage", "nocUsage"].includes(
                                  table?.name
                                ) ? (
                                <RenderUsageChart
                                  table={table}
                                  customerSelectedRow={customerSelectedRow}
                                />
                              ) : table?.name === "permitPackages" ? (
                                <RenderCustomerPermits
                                  packagePage={packagePage}
                                  setPackagePage={setPackagePage}
                                  packagePageSize={packagePageSize}
                                  setpackagePageSize={setpackagePageSize}
                                  packageLoading={packageLoading}
                                  setPackageLoading={setPackageLoading}
                                  customerSelectedRow={customerSelectedRow}
                                  packageRowCount={packageRowCount}
                                  setPackageRowCount={setPackageRowCount}
                                  selectedPackage={selectedPackage}
                                  setSelectedPackage={setSelectedPackage}
                                />
                              ) : (
                                <Table>
                                  <TableBody>
                                    {table?.data?.map((row, index) => (
                                      <TableRow key={index}>
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          {row.label}
                                        </TableCell>
                                        <TableCell align="right">
                                          {row.value}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
      <RemoteConnectionModal
        remoteModalOpen={remoteModalOpen}
        closeRemoteModal={closeRemoteModal}
        customerSelectedRow={customerSelectedRow}
        remoteCredentials={remoteCredentials}
        setRemoteCredentials={setRemoteCredentials}
        handleRemoteConnect={handleRemoteConnect}
        connectionLoading={connectionLoading}
        connectionMsg={connectionMsg}
      />
    </Box>
  );
};

export default CustomerInfoCard;
